import { useState, useEffect, useRef } from 'react'
import type SwiperCore from 'swiper'
import { Carousel } from '#app/shared/ui/data-display/carousel/Carousel.js'
import { Icon } from '#app/shared/ui/icons/Icon.js'

type Props = {
  slides: React.ReactNode[]
  isLoading?: boolean
}

export const CarDescriptionMobile = ({ slides, isLoading = false }: Props) => {
  const MOBILE_SPACE_BETWEEN = -4
  const DESKTOP_SPACE_BETWEEN = 12
  const [swiper, setSwiper] = useState<SwiperCore | undefined>(undefined)
  const [currentSliderIndex, setCurrentSliderIndex] = useState(0)

  // calculate dynamically the number of slides per view based on the container width
  const [slidesPerView, setSlidesPerView] = useState(3)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateSlidesPerView = () => {
      const availableWidth = container.clientWidth
      const slideWidth = 320
      const isDesktop =
        typeof window !== 'undefined' && window.innerWidth >= 1310
      const slideGap =
        typeof window !== 'undefined' && window.innerWidth >= 640
          ? DESKTOP_SPACE_BETWEEN
          : MOBILE_SPACE_BETWEEN

      if (isDesktop) {
        setSlidesPerView(4)
        return
      }

      const slidesCount = availableWidth / (slideWidth + slideGap)
      setSlidesPerView(Math.max(1, Number(slidesCount.toFixed(2))))
    }

    const resizeObserver = new ResizeObserver(updateSlidesPerView)
    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const handlePrevClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    swiper?.slidePrev()
  }

  const handleNextClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    swiper?.slideNext()
  }

  return (
    <div
      className={`
        relative -mr-4 w-[calc(100%+1rem)]

        sm:mr-0 sm:w-full
      `}
      ref={containerRef}
    >
      <Carousel
        slides={slides}
        spaceBetween={MOBILE_SPACE_BETWEEN}
        breakpoints={{
          640: {
            spaceBetween: DESKTOP_SPACE_BETWEEN,
          },
        }}
        slidesPerView={slidesPerView}
        className="w-full"
        swiper={swiper}
        setSwiper={setSwiper}
        activeIndex={currentSliderIndex}
        onActiveIndexChange={setCurrentSliderIndex}
      />

      {!isLoading && (
        <>
          <button
            type="button"
            className={`
              absolute -left-3 top-1/2 z-10 flex h-[36px] min-h-[36px] w-[36px]
              min-w-[36px] -translate-y-5 items-center justify-center
              rounded-full border border-neutral-100

              lg:-left-5

              ${currentSliderIndex < 1 ? 'hidden' : 'bg-white'}
            `}
            disabled={currentSliderIndex < 1}
            onClick={handlePrevClick}
          >
            <Icon
              size="12px"
              name="basic/chevron-left"
              className={`
                pointer-events-none

                ${
                  currentSliderIndex <= 0
                    ? 'text-purple-300'
                    : `text-neutral-300`
                }
              `}
            />
          </button>

          {/* Right Arrow Button */}
          <button
            type="button"
            className={`
              absolute right-0 top-1/2 z-10 flex h-[36px] min-h-[36px] w-[36px]
              min-w-[36px] -translate-y-5 cursor-pointer items-center
              justify-center rounded-full border border-neutral-100

              lg:-right-4 lg:group-hover:opacity-100

              ${
                currentSliderIndex + Math.round(slidesPerView) >= slides.length
                  ? `hidden`
                  : `bg-white`
              }
            `}
            disabled={
              currentSliderIndex + Math.round(slidesPerView) >= slides.length
            }
            onClick={handleNextClick}
          >
            <Icon
              size="12px"
              name="basic/chevron-right"
              className={`
                pointer-events-none

                ${
                  currentSliderIndex >= slides.length - 1
                    ? 'text-purple-300'
                    : 'text-neutral-300'
                }
              `}
            />
          </button>
        </>
      )}
    </div>
  )
}
